import React from 'react';
import { useAuth } from './components/auth/AuthProvider';
import Navigation from './components/Navigation';
import JournalPage from './components/journal/JournalPage';
import GuidedChat from './components/guided-chat/GuidedChat';
import ProfileSettings from './components/settings/ProfileSettings';
import PartsFamily from './components/parts/PartsFamily';
import AuthModal from './components/auth/AuthModal';
import LandingPage from './components/LandingPage';
import ExhaleButton from './components/support/ExhaleButton';
import { ViewType } from './types';
import { LazyMotion, domAnimation } from 'framer-motion';
import { supabase } from './lib/supabase';

function App() {
  const { user } = useAuth();
  const [currentView, setCurrentView] = React.useState<ViewType>('journal');
  const [showModal, setShowModal] = React.useState(false);
  const [isSignUp, setIsSignUp] = React.useState(false);

  // Handle auth callback
  React.useEffect(() => {
    // Check if we're on the callback URL
    const isCallback = window.location.pathname === '/auth/callback';
    if (isCallback) {
      // Get hash parameters
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const expiresIn = hashParams.get('expires_in');
      const type = hashParams.get('type');

      if (accessToken && refreshToken) {
        // Set the session
        supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: parseInt(expiresIn || '3600')
        }).then(() => {
          // Redirect to home after setting session
          window.location.href = '/';
        });
      } else {
        // If no tokens found, redirect to home
        window.location.href = '/';
      }
    }
  }, []);

  if (!user) {
    return (
      <LazyMotion features={domAnimation}>
        <LandingPage onGetStarted={() => setShowModal(true)} />
        {showModal && (
          <AuthModal 
            onClose={() => setShowModal(false)} 
            isSignUp={isSignUp} 
            setIsSignUp={setIsSignUp} 
          />
        )}
      </LazyMotion>
    );
  }

  const renderView = () => {
    switch (currentView) {
      case 'journal':
        return <JournalPage />;
      case 'guided-chat':
        return <GuidedChat />;
      case 'parts':
        return <PartsFamily />;
      case 'settings':
        return <ProfileSettings />;
      default:
        return <JournalPage />;
    }
  };

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-midnight-50">
        <Navigation currentView={currentView} onViewChange={setCurrentView} />
        <main className="pt-6">
          {renderView()}
        </main>
        <ExhaleButton />
      </div>
    </LazyMotion>
  );
}

export default App;